/** Validação de CNPJ (numérico ou alfanumérico) e CPF (dígitos verificadores). Funções puras. */
import { limparDocumento, soDigitos } from './comum.js';

/** Valor de cada caractere no cálculo do DV: '0'-'9' → 0-9, 'A'-'Z' → 17-42 (código ASCII − 48), regra do CNPJ alfanumérico. */
const valor = (ch: string) => ch.charCodeAt(0) - 48;

function dv(base: string, pesos: number[]): number {
  const soma = base.split('').reduce((acc, ch, i) => acc + valor(ch) * (pesos[i] ?? 0), 0);
  const r = soma % 11;
  return r < 2 ? 0 : 11 - r;
}

/** CNPJ numérico (12.345.678/0001-95) ou alfanumérico (12.ABC.345/01DE-35): 12 posições [0-9A-Z] + 2 dígitos verificadores. */
export function cnpjValido(v: string): boolean {
  const d = limparDocumento(v);
  if (!/^[0-9A-Z]{12}\d{2}$/.test(d) || /^(.)\1{13}$/.test(d)) return false;
  const p1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  const p2 = [6, ...p1];
  return dv(d.slice(0, 12), p1) === Number(d[12]) && dv(d.slice(0, 13), p2) === Number(d[13]);
}

export function cpfValido(v: string): boolean {
  const d = soDigitos(v);
  if (d.length !== 11 || /^(\d)\1{10}$/.test(d)) return false;
  const p1 = [10, 9, 8, 7, 6, 5, 4, 3, 2];
  const p2 = [11, ...p1];
  return dv(d.slice(0, 9), p1) === Number(d[9]) && dv(d.slice(0, 10), p2) === Number(d[10]);
}

/** (11) 92222-2222 · (11) 2222-2222; o que não for telefone volta como veio */
export function formatarTelefone(v: string): string {
  const d = v.replace(/\D/g, '');
  if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  if (d.length === 9) return `${d.slice(0, 5)}-${d.slice(5)}`;
  if (d.length === 8) return `${d.slice(0, 4)}-${d.slice(4)}`;
  return v;
}
/** 4711302 → 4711-3/02 (o texto depois do código fica como está) */
export const formatarCnae = (v: string): string => v.replace(/^(\d{4})(\d)(\d{2})(?!\d)/, '$1-$2/$3');
/** 2062 → 206-2 */
export const formatarNaturezaJuridica = (v: string): string => v.replace(/^(\d{3})(\d)(?!\d)/, '$1-$2');

export function formatarDocumento(v: string): string {
  const d = limparDocumento(v);
  if (d.length === 14) return d.replace(/^(.{2})(.{3})(.{3})(.{4})(\d{2})$/, '$1.$2.$3/$4-$5');
  if (d.length === 11 && /^\d+$/.test(d)) return d.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
  return v;
}

/** Competência AAAAMM → "mm/aaaa". */
export function formatarCompetencia(c: number): string {
  const ano = Math.floor(c / 100);
  const mes = c % 100;
  return `${String(mes).padStart(2, '0')}/${ano}`;
}

export function competenciaDe(ano: number, mes: number): number {
  return ano * 100 + mes;
}

export function competenciaAtual(hoje = new Date()): number {
  return competenciaDe(hoje.getFullYear(), hoje.getMonth() + 1);
}
